import { useEffect, useState } from 'react';

export type NotificationKind = 'auth-expired' | 'error' | 'info';

export interface Notification {
  id: number;
  kind: NotificationKind;
  message: string;
}

const notifyEvents = new EventTarget();
let nextId = 1;

function dispatch(kind: NotificationKind, message: string): void {
  const n: Notification = { id: nextId++, kind, message };
  notifyEvents.dispatchEvent(new CustomEvent('notify', { detail: n }));
}

export function notifyAuthExpired(): void {
  dispatch('auth-expired', 'Your session has expired. Please log in again.');
}

export function notifyError(message: string): void {
  dispatch('error', message);
}

export function notifyInfo(message: string): void {
  dispatch('info', message);
}

/**
 * Subscribe to the global notification stream.
 *
 * Returns the current notification (most recent un-dismissed) and a dismiss()
 * callback. `auth-expired` notifications are sticky (no auto-timeout);
 * `error` and `info` auto-dismiss after 5 s and 3 s respectively.
 */
export function useNotify(): { current: Notification | null; dismiss: () => void } {
  const [current, setCurrent] = useState<Notification | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const n = (e as CustomEvent<Notification>).detail;
      setCurrent((prev) => {
        if (prev?.kind === 'auth-expired') return prev;
        return n;
      });
    };
    notifyEvents.addEventListener('notify', handler);
    return () => notifyEvents.removeEventListener('notify', handler);
  }, []);

  useEffect(() => {
    if (!current || current.kind === 'auth-expired') return;
    const timeoutMs = current.kind === 'error' ? 5000 : 3000;
    const t = setTimeout(() => {
      setCurrent((c) => (c?.id === current.id ? null : c));
    }, timeoutMs);
    return () => clearTimeout(t);
  }, [current]);

  const dismiss = () => setCurrent(null);

  return { current, dismiss };
}
